// @bzapper/i18n — 6 idiomas + locale mundial (BCP-47/CLDR).
// - Strings traduzidas via ICU MessageFormat, com cadeia de fallback → pt-BR.
// - Formatação (data/número/moeda/relativo) locale-aware para QUALQUER locale BCP-47.
// - Erros = código neutro estável + mensagem traduzida (nunca dar parse em texto).
import { IntlMessageFormat } from 'intl-messageformat';

import ptBR from './locales/pt-BR/common.json' with { type: 'json' };
import en from './locales/en/common.json' with { type: 'json' };
import es from './locales/es/common.json' with { type: 'json' };
import it from './locales/it/common.json' with { type: 'json' };
import de from './locales/de/common.json' with { type: 'json' };
import fr from './locales/fr/common.json' with { type: 'json' };
// Catálogo do painel admin (namespace "admin.*"), por idioma.
import adminPtBR from './locales/pt-BR/admin.json' with { type: 'json' };
import adminEn from './locales/en/admin.json' with { type: 'json' };
import adminEs from './locales/es/admin.json' with { type: 'json' };
import adminIt from './locales/it/admin.json' with { type: 'json' };
import adminDe from './locales/de/admin.json' with { type: 'json' };
import adminFr from './locales/fr/admin.json' with { type: 'json' };

/** Idiomas suportados. pt-BR é o padrão e o fallback final. */
export const LOCALES = ['pt-BR', 'en', 'es', 'it', 'de', 'fr'] as const;
export type Locale = (typeof LOCALES)[number];

/** Idiomas com cobertura completa (tier-1). Os demais caem para pt-BR no que faltar. */
export const TIER1_LOCALES: Locale[] = ['pt-BR', 'en', 'es'];
export const DEFAULT_LOCALE: Locale = 'pt-BR';

type RawCatalog = Record<string, unknown>;
const RAW: Record<Locale, RawCatalog> = {
  'pt-BR': ptBR,
  en,
  es,
  it,
  de,
  fr,
};
const ADMIN_RAW: Record<Locale, RawCatalog> = {
  'pt-BR': adminPtBR,
  en: adminEn,
  es: adminEs,
  it: adminIt,
  de: adminDe,
  fr: adminFr,
};

function flatten(obj: RawCatalog, prefix = '', out: Record<string, string> = {}) {
  for (const [k, v] of Object.entries(obj)) {
    if (k.startsWith('$')) continue; // metadados ($locale, $name)
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object') flatten(v as RawCatalog, key, out);
    else if (typeof v === 'string') out[key] = v;
  }
  return out;
}

// Cada catálogo é mesclado SOBRE o pt-BR (fallback de chaves faltantes).
// Inclui common.* + admin.* (este último prefixado com "admin").
const BASE = {
  ...flatten(RAW[DEFAULT_LOCALE]),
  ...flatten(ADMIN_RAW[DEFAULT_LOCALE], 'admin'),
};
const CATALOGS: Record<Locale, Record<string, string>> = Object.fromEntries(
  LOCALES.map((l) => [
    l,
    { ...BASE, ...flatten(RAW[l]), ...flatten(ADMIN_RAW[l], 'admin') },
  ]),
) as Record<Locale, Record<string, string>>;

/** True se `value` é um locale suportado. */
export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

/**
 * Resolve o melhor locale: request (Accept-Language BCP-47) → padrão do tenant → pt-BR.
 * Casa por idioma base (ex.: "pt-PT" → "pt-BR", "en-US" → "en").
 */
export function negotiateLocale(acceptLanguage?: string | null, tenantDefault?: string): Locale {
  const candidates: string[] = [];
  if (acceptLanguage) {
    for (const part of acceptLanguage.split(',')) {
      const tag = part.split(';')[0]?.trim();
      if (tag) candidates.push(tag);
    }
  }
  if (tenantDefault) candidates.push(tenantDefault);
  for (const tag of candidates) {
    if (isLocale(tag)) return tag;
    const base = tag.toLowerCase().split('-')[0];
    const match = LOCALES.find((l) => l.toLowerCase().split('-')[0] === base);
    if (match) return match;
  }
  return DEFAULT_LOCALE;
}

export type MessageValues = Record<string, string | number | Date>;

/** Cria um tradutor ICU para um locale, com fallback de chaves para pt-BR. */
export function createTranslator(locale: Locale) {
  const catalog = CATALOGS[locale] ?? CATALOGS[DEFAULT_LOCALE];
  return function t(key: string, values?: MessageValues): string {
    const msg = catalog[key];
    if (msg == null) return key; // chave ausente: devolve a chave (visível em dev)
    if (!values && !msg.includes('{')) return msg;
    try {
      return new IntlMessageFormat(msg, locale).format(values) as string;
    } catch {
      return msg;
    }
  };
}

/**
 * Mensagem de erro: código neutro estável + texto traduzido.
 * A lógica usa SEMPRE o `code`; o `message` é só para humanos.
 */
export function errorMessage(code: string, locale: Locale, values?: MessageValues) {
  const t = createTranslator(locale);
  const key = `errors.${code}`;
  const catalog = CATALOGS[locale] ?? CATALOGS[DEFAULT_LOCALE];
  const message = catalog[key] != null ? t(key, values) : t('errors.internal_error');
  return { code, message, locale };
}

/** Formatadores locale-aware (CLDR via Intl) — funcionam para qualquer BCP-47. */
export function formatters(locale: string) {
  return {
    number: (n: number, opts?: Intl.NumberFormatOptions) =>
      new Intl.NumberFormat(locale, opts).format(n),
    currency: (n: number, currency: string) =>
      new Intl.NumberFormat(locale, { style: 'currency', currency }).format(n),
    date: (d: Date | number, opts?: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat(locale, opts ?? { dateStyle: 'medium' }).format(d),
    relativeTime: (value: number, unit: Intl.RelativeTimeFormatUnit) =>
      new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(value, unit),
  };
}
